<template>
  <div>
    <div class="dashboard-payment">
      <div class="dashboard-payment__wrap">
        <h2 v-if="account.trialEnabled && next" class="dashboard-payment__title mb-3">
          {{ $t('trialPeriod') }}
        </h2>
        <h2 v-else-if="next" class="dashboard-payment__title">
          {{ next.paymentPrice ? $t('nextPayment') : $t('nextPlan') }}
        </h2>
        <div v-if="account.trialEnabled && next">
          <div>{{ $t('connectedTrialPlan') }} «{{ account.plan.title }}»</div>
          <div class="mt-5 dashboard-payment__subtitle">
            <div class="mb-1">
              {{ $t('currentPeriod') }}
            </div>
            {{ periodText }}
          </div>
          <CancelSubscription>
            <template v-slot:button="{ on, attrs }">
              <VBtn
                v-bind="attrs"
                v-on="on"
                :disabled="!account.trialEnabled"
                color="red"
                class="mt-7 mb-12"
              >
                {{ $t('disableTrialPeriod') }}
              </VBtn>
            </template>
            <template v-slot:title>
              {{ $t('trialCancelDialogTitle') }}
            </template>
            <template v-slot:paidFor>
              {{ $t('trialCancelDialog') }}
            </template>
            <template v-slot:text>
              {{ $t('trialCancelDialog2') }}
            </template>
            <template v-slot:btnName>
              {{ $t('disableTrialPeriod') }}
            </template>
          </CancelSubscription>
          <div class="dashboard-payment__item" v-if="account.trialEnabled && next && next.paymentPrice && subscriptionStatus">
            <div class="dashboard-payment__caption">
              {{ $t('method') }}
            </div>
            <div class="dashboard-payment__value">
              <PaymentMethodResolver active empty>
                <template #empty>
                  {{ $t('emptyMethod') }}
                  <button class="dashboard-payment__change" @click="changePaymentMethod">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 14.5017V17.0017H7.5L14.8733 9.62833L12.3733 7.12833L5 14.5017ZM16.8067 7.695C17.0667 7.435 17.0667 7.015 16.8067 6.755L15.2467 5.195C14.9867 4.935 14.5667 4.935 14.3067 5.195L13.0867 6.415L15.5867 8.915L16.8067 7.695Z"
                        fill="#0A82D9"
                      />
                    </svg>
                  </button>
                </template>
                <template v-slot:default="{method}">
                  {{ method.title }}
                  <button class="dashboard-payment__change" @click="changePaymentMethod">
                    <svg
                      width="22"
                      height="22"
                      viewBox="0 0 22 22"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M5 14.5017V17.0017H7.5L14.8733 9.62833L12.3733 7.12833L5 14.5017ZM16.8067 7.695C17.0667 7.435 17.0667 7.015 16.8067 6.755L15.2467 5.195C14.9867 4.935 14.5667 4.935 14.3067 5.195L13.0867 6.415L15.5867 8.915L16.8067 7.695Z"
                        fill="#0A82D9"
                      />
                    </svg>
                  </button>
                </template>
              </PaymentMethodResolver>
            </div>
          </div>
          <div class="dashboard-payment__item" v-if="account.trialEnabled && next && next.paymentPrice">
            <div class="dashboard-payment__caption">
              {{ $t('date') }}
            </div>
            <div class="dashboard-payment__value">
              <template v-if="subscriptionStatus">
                {{ nextPaymentText }}
              </template>
              <template v-else>
                {{ $t('subscriptionCanceled') }}
              </template>
            </div>
          </div>
          <div class="dashboard-payment__item-trial-text" v-if="account.trialEnabled && next && next.paymentPrice">
            <div class="dashboard-payment__caption">
              <div class="mb-2">
                {{ $t('trialText.1') }}
              </div>
              <div>
                {{ $t('trialText.2') }}
              </div>
            </div>
            <VBtn
              :disabled="!account.trialEnabled"
              color="success"
              class="mt-7 mb-12"
              @click="activatePlan"
            >
              {{ $t('activePlan') }}
            </VBtn>
          </div>
        </div>
        <div v-else>
          <div class="dashboard-payment__items-wrap">
            <div class="dashboard-payment__item">
              <div class="dashboard-payment__caption">
                {{ $t('plan') }}
              </div>
              <div class="dashboard-payment__value">
                «{{ nextPlanText }}»
                <button @click="changePlan" class="dashboard-payment__change">
                  <svg
                    width="22px"
                    height="22px"
                    viewBox="0 0 22 22"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M5 14.5017V17.0017H7.5L14.8733 9.62833L12.3733 7.12833L5 14.5017ZM16.8067 7.695C17.0667 7.435 17.0667 7.015 16.8067 6.755L15.2467 5.195C14.9867 4.935 14.5667 4.935 14.3067 5.195L13.0867 6.415L15.5867 8.915L16.8067 7.695Z" fill="#0A82D9" />
                  </svg>
                </button>
              </div>
            </div>
            <div class="dashboard-payment__item" v-if="next && next.paymentPrice">
              <div class="dashboard-payment__caption">
                {{ $t('period') }}
              </div>
              <div class="dashboard-payment__value">
                <PaymentPeriodChange />
              </div>
            </div>
            <div class="dashboard-payment__item" v-if="next && next.paymentPrice && subscriptionStatus">
              <div class="dashboard-payment__caption">
                {{ $t('method') }}
              </div>
              <div class="dashboard-payment__value">
                <PaymentMethodResolver active empty>
                  <template #empty>
                    {{ $t('emptyMethod') }}
                    <button class="dashboard-payment__change" @click="changePaymentMethod">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 14.5017V17.0017H7.5L14.8733 9.62833L12.3733 7.12833L5 14.5017ZM16.8067 7.695C17.0667 7.435 17.0667 7.015 16.8067 6.755L15.2467 5.195C14.9867 4.935 14.5667 4.935 14.3067 5.195L13.0867 6.415L15.5867 8.915L16.8067 7.695Z"
                          fill="#0A82D9"
                        />
                      </svg>
                    </button>
                  </template>
                  <template v-slot:default="{method}">
                    {{ method.title }}
                    <button class="dashboard-payment__change" @click="changePaymentMethod">
                      <svg
                        width="22"
                        height="22"
                        viewBox="0 0 22 22"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          d="M5 14.5017V17.0017H7.5L14.8733 9.62833L12.3733 7.12833L5 14.5017ZM16.8067 7.695C17.0667 7.435 17.0667 7.015 16.8067 6.755L15.2467 5.195C14.9867 4.935 14.5667 4.935 14.3067 5.195L13.0867 6.415L15.5867 8.915L16.8067 7.695Z"
                          fill="#0A82D9"
                        />
                      </svg>
                    </button>
                  </template>
                </PaymentMethodResolver>
              </div>
            </div>
            <PaymentMethodResolver active v-if="next && next.paymentPrice">
              <template v-slot:default="{method}">
                <div v-if="method.failedAt" class="dashboard-payment-failed">
                  <svg
                    width="30"
                    height="26"
                    viewBox="0 0 30 26"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path d="M28.9999 14.3637L26.4544 11.8182L22.6362 15.6365L18.8179 11.8182L16.2725 14.3637L20.0907 18.1819L16.2725 22.0002L18.8179 24.5457L22.6362 20.7274L26.4544 24.5457L28.9999 22.0002L25.1817 18.1819L28.9999 14.3637Z" stroke="#EA0D0D" stroke-miterlimit="10" />
                    <path d="M28.3639 10.5456V2.90911C28.3639 1.82728 27.5367 1 26.5185 1H2.97275C1.89092 1 1 1.82728 1 2.90911V18.182C1 19.2638 1.89092 20.0911 2.97275 20.0911H15.0002" stroke="#EA0D0D" stroke-miterlimit="10" />
                    <path d="M1 4.81812H28.3639" stroke="#EA0D0D" stroke-miterlimit="10" />
                    <path d="M1 8.63629H28.3639" stroke="#EA0D0D" stroke-miterlimit="10" />
                    <path d="M2.91016 11.182H7.36475" stroke="#EA0D0D" stroke-miterlimit="10" />
                  </svg>
                  {{ $t('failed') }}
                </div>
              </template>
            </PaymentMethodResolver>
            <div class="dashboard-payment__item" v-if="next && next.paymentPrice">
              <div class="dashboard-payment__caption">
                {{ $t('date') }}
              </div>
              <div class="dashboard-payment__value">
                <template v-if="subscriptionStatus">
                  {{ nextPaymentText }}
                </template>
                <template v-else>
                  {{ $t('subscriptionCanceled') }}
                </template>
              </div>
            </div>
            <div class="dashboard-payment__item" v-if="subscriptionStatus">
              <div class="dashboard-payment__caption">
                {{ $t('dateApply') }}
              </div>
              <div class="dashboard-payment__value">
                <template v-if="subscriptionStatus">
                  {{ expirationText }}
                </template>
                <template v-else>
                  {{ $t('subscriptionCanceled') }}
                </template>
              </div>
            </div>
            <div v-if="subscriptionStatus" class="dashboard-payment__item">
              <div class="dashboard-payment__caption">
                {{ $t('monthPrice') }}
              </div>
              <div class="dashboard-payment__value">
                {{ !monthPrice ? $t('free') : formatCurrencyFromMoney(monthPrice) }}
              </div>
            </div>
            <div class="dashboard-payment__item" v-if="next && next.paymentPrice && subscriptionStatus">
              <div class="dashboard-payment__caption">
                {{ $t('discount') }}
              </div>
              <div class="dashboard-payment__value">
                {{ next.paymentPeriod.discountPercent }}%
              </div>
            </div>
          </div>
          <div class="dashboard-payment__item dashboard-payment__item_total" v-if="next && next.paymentPrice && subscriptionStatus">
            <div class="dashboard-payment__caption">
              {{ $t('total') }}
            </div>
            <div class="dashboard-payment__value">
              <div v-if="next && next.paymentPrice.value !== next.paymentPriceWithDiscount.value" class="dashboard-payment__sale-price">
                {{ formatCurrencyFromMoney(next.paymentPrice) }}
              </div>
              <div v-if="next" class="dashboard-payment__price">
                {{ formatCurrencyFromMoney(next.paymentPriceWithDiscount) }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
    <template v-if="next && next.paymentPrice && !account.trialEnabled">
      <RenewSubscription />
      <CancelSubscription />
    </template>
  </div>
</template>

<script>
import useAccount from '@/compositions/accounting/useAccount';
import useNumberFormatter from '@/compositions/useNumberFormatter';
import useSubscription from '@/compositions/accounting/useSubscription';
import { paymentTabs, settingsTabs } from '@/enums';
import PaymentPeriodChange from '@/components/Payment/Periods/PaymentPeriodChange';
import { computed } from '@vue/composition-api';
import RenewSubscription from '@/components/Payment/Subscription/RenewSubscription';
import CancelSubscription from '@/components/Payment/Subscription/CancelSubscription';
import useDateFormatter from '@/compositions/useDateFormatter';
import PaymentMethodResolver from '@/components/Payment/Methods/PaymentMethodResolver';

export default {
  name: 'PaymentDashboardDetails',
  components: {
    PaymentMethodResolver,
    CancelSubscription,
    RenewSubscription,
    PaymentPeriodChange,
  },
  setup(_, { root: { $router } }) {
    const {
      account, nextPaymentText, nextPlanText, expirationText, periodText, plan,
    } = useAccount();
    const { subscriptionStatus } = useSubscription();
    const { formatCurrencyFromMoney } = useNumberFormatter();
    const next = computed(() => account.value.next);

    const monthPrice = computed(() => account.value?.next?.plan?.price);

    function changePlan() {
      $router.push({ name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.PLANS } });
    }

    const { formatDateDefault } = useDateFormatter();

    function changePaymentMethod() {
      $router.push({ name: 'settings', params: { type: settingsTabs.PAYMENT, addType: paymentTabs.INFO } });
    }

    function activatePlan() {
      $router.replace({
        name: 'order',
        params: {
          id: plan.value.id,
          type: 'plan',
        },
      });
    }

    return {
      nextPlanText,
      nextPaymentText,
      next,
      formatCurrencyFromMoney,
      changePlan,
      changePaymentMethod,
      subscriptionStatus,
      account,
      monthPrice,
      formatDateDefault,
      expirationText,
      periodText,
      activatePlan,
    };
  },
};
</script>

<style lang="scss" scoped>
.dashboard-payment {
    padding: 2.3125rem 1.875rem 1.75rem 2.5rem;
    border: 1px solid #EEEEEE;
    border-radius: 0.5rem;
    margin-bottom: 1.375rem;
  &__items-wrap {
    margin-bottom: 0.5rem;
  }
  &__item {
    display: flex;
    justify-content: space-between;
    position: relative;
    color: #212121;
    font-weight: 400;
    line-height: 1.375;
    margin-bottom: 1.25rem;
    overflow: hidden;
    &:after {
      content: '';
      background: url("../../../assets/img/account/plan/dashboard-details-bg.svg") center;
      width: 100%;
      height: 1px;
      position: absolute;
      left: 0;
      bottom: 0.375rem;
      z-index: 1;
    }
    &_total {
      padding-top: 0.5rem;
      align-items: flex-end;
    }
  }
  &__item-trial-text {
    display: flex;
    flex-direction: column;
    button {
      width: fit-content;
    }
  }
  &__caption {
    background: #FFFFFF;
    position: relative;
    z-index: 2;
  }
  &__value {
    background: #FFFFFF;
    position: relative;
    z-index: 2;
  }
  &__title {
    font-size: 1.5rem;
    line-height: 1.2;
    color: #212121;
    margin-bottom: 2rem;
  }
  &__subtitle {
    font-size: 1rem;
    line-height: 1.2;
    color: #212121;
    opacity: 40%;
  }
  &__change {
    display: inline-block;
    width: 1.375rem;
    height: 1.375rem;
    vertical-align: middle;
  }
  &__total-wrap {
    height: 11.25rem;
    padding: 1.25rem 0;
    font-weight: 500;
    border-top: 1px solid #EEEEEE;
  }
  &__sale-price {
    text-align: right;
    text-decoration: line-through;
    color: rgb(33 33 33 / 50%);
    margin-bottom: 0.1875rem;
  }
  &__price {
    font-size: 1.5rem;
    line-height: 1.3;
  }
  &__cancel-btn {
    width: 100%;
    max-width: 17.5rem;
    padding: 0.4325rem 0.875rem;
    border: 1px solid #EEEEEE;
  }
}
.dashboard-payment-failed {
  display: flex;
  align-items: center;
  padding: 0.625rem 1.375rem;
  background: #fef3f3;
  border: 1px solid rgba(234, 13, 13, 0.2);
  box-sizing: border-box;
  border-radius: 0.5rem;
  font-size: 0.875rem;
  line-height: 1.14;
  color: #EA0D0D;
  position: relative;
  z-index: 2;
  margin-top: 0.5rem;
  margin-bottom: 0.9375rem;
  svg {
    min-width: 1.75rem;
    margin-right: 1.25rem;
  }
  &:before {
    content: '';
    position: absolute;
    width: 0.75rem;
    height: 0.75rem;
    right: 3.25rem;
    top: -0.4375rem;
    background: #fef3f3;
    border-top: 1px solid rgba(234, 13, 13, 0.2);
    border-right: 1px solid rgba(234, 13, 13, 0.2);
    transform: rotate(-45deg);
  }
}
@media #{map-get($display-breakpoints, 'md-and-down')} {
  .dashboard-payment {
    position: relative;
    &:before, &:after {
      content: '';
      height: 1px;
      background-color: #EEEEEE;
      position: absolute;
      left: 0;
      right: 0;
      top: 0;
    }
    &:after {
      top: inherit;
      bottom: 0;
    }
    border: none;
    &__wrap {
      max-width: 30.25rem;
      margin: 0 auto;
    }
    &__title {
      font-size: 1.25rem;
      margin-bottom: 1.5rem;
    }
    &__item {
      font-size: 0.9375rem;
      margin-bottom: 0.75rem;
    }
    &__price {
      font-size: 1.25rem;
    }
  }
}
@media #{map-get($display-breakpoints, 'xs-only')} {
  .dashboard-payment {
    &__wrap {
      max-width: 20rem;
    }
    padding: 1.25rem;
    &__title {
      font-size: 1rem;
      margin-bottom: 1rem;
    }
    &__item {
      font-size: 0.8125rem;
      margin-bottom: 0.25rem;
      &:after {
        content: '';
        background: url("../../../assets/img/account/plan/dashboard-details-bg.svg") center;
        width: 100%;
        height: 1px;
        position: absolute;
        left: 0;
        bottom: 0.375rem;
        z-index: 1;
      }
      &_total {
        padding-top: 0.5rem;
        align-items: flex-end;
      }
    }
    &__price {
      font-size: 1.125rem;
    }
  }
  .dashboard-payment-failed {
    font-size: 0.6875rem;
    svg {
      min-width: 1.5rem;
    }
    &:before {
      right: 2.5rem;
      top: -0.4375rem;
    }
  }
}

</style>

<i18n lang="yml" locale="ru">
  emptyMethod: Не установлен
  cancel: Отмена
  nextPayment: Следующий платеж
  nextPlan: Следующий тариф
  plan: Тариф
  free: Бесплатно
  period: Платёжный период
  method: Способ оплаты
  date: Дата платежа
  dateApply: Дата начала периода
  monthPrice: Стоимость в месяц
  discount: Скидка
  total: Итого
  failed: Последняя попытка списания с выбранной карты была безуспешной
  cancelSubscription: Отменить подписку
  renewSubscription: Возобновить подписку
  subscriptionCanceled: Подписка отменена
  connectedTrialPlan: У вас подключен пробный период тарифа
  trialPeriod: Пробный период
  currentPeriod: 'Текущий период:'
  disableTrialPeriod: Отключить пробный период
  trialCancelDialogTitle: Отмена пробного периода
  trialCancelDialog: Ваш пробный период активирован
  trialCancelDialog2: 'После окончания пробного периода Вы перейдете на бесплатный тариф «Промо».
  Если вы отмените пробный период сегодня, то сможете пользоваться сервисом до указанной даты. Счёт за следующий период выставлен не будет.'
  trialText: {
    "1": "В рамках тестового периода отправка СМС и звонки клиентам недоступны.",
    "2": "Для доступ ко всем функциям плана, перейдите на платный тариф."
  }
  activePlan: Активировать план
</i18n>
<i18n lang="yml" locale="en">
  emptyMethod: Not set
  cancel: Cancel
  nextPayment: Next payment
  nextPlan: Next plan
  plan: Plan
  free: Free
  period: Payment period
  method: Payment method
  date: Payment date
  dateApply: Next period starts
  monthPrice: Monthly price
  discount: Discount
  total: Total
  failed: Last payment was unsuccessful
  cancelSubscription: Cancel subscription
  renewSubscription: Renew subscription
  subscriptionCanceled: Subscription canceled
  connectedTrialPlan: You have activated the trial period of the tariff
  trialPeriod: Trial period
  currentPeriod: 'Current period:'
  disableTrialPeriod: Disable the trial period
  trialCancelDialogTitle: Cancellation of the trial period
  trialCancelDialog: Your trial period is activated
  trialCancelDialog2: 'At the end of the trial period, you will automatically be switched to the free "Promo" tariff.
  If you cancel the trial period today, you will be able to use the service until the specified expiration date. No invoice will be issued for the next period.'
  trialText: {
    "1": "During the trial period SMS sending and client calls are not available.",
    "2": "To access all features of the plan, switch to a paid plan."
  }
  activePlan: Activate the plan
</i18n>
